import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { getSharedFiles, deleteSharedFiles } from '../utils/sharedFiles';
import CropModal from './CropModal';

interface UploadModalProps {
  onClose: () => void;
  onUploadComplete: () => void;
}

interface Tag {
  value: string;
  type: 'person' | 'place' | 'thing';
}

interface TagWithCount {
  tag: string;
  type: 'person' | 'place' | 'thing';
  count: number;
}

// Helper function to compute hash using Web Crypto API (much faster than BMF)
// Handles mobile file access issues by using FileReader as fallback
async function computeMD5(file: File): Promise<string> {
  try {
    // Try modern arrayBuffer() first (fast on desktop)
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  } catch (error) {
    // Fallback for mobile/stale file references: use FileReader
    console.warn('file.arrayBuffer() failed, using FileReader fallback:', error);
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
          resolve(hashHex);
        } catch (err) {
          reject(err);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file. Please try selecting the file again.'));
      };
      
      reader.readAsArrayBuffer(file);
    });
  }
}

export default function UploadModal({ onClose, onUploadComplete }: UploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [duplicate, setDuplicate] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Cached file data to prevent permission expiration on mobile
  const [cachedFileData, setCachedFileData] = useState<Blob | null>(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  
  // Cropping
  const [showCropModal, setShowCropModal] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(0);
  
  // Tags
  const [tags, setTags] = useState<Tag[]>([]);
  const [newTag, setNewTag] = useState('');
  const [newTagType, setNewTagType] = useState<'person' | 'place' | 'thing'>('person');
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  
  // Audio recording
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [hasAudio, setHasAudio] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioPreviewRef = useRef<HTMLAudioElement | null>(null);
  const sharedFileIdsRef = useRef<string[]>([]);

  // Fetch all existing tags for autocomplete
  const { data: allTagsData } = useQuery({
    queryKey: ['all-tags'],
    queryFn: () => api.listAllTags(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const existingTags: TagWithCount[] = allTagsData?.tags || [];

  // Load shared files from Service Worker on mount
  useEffect(() => {
    const loadSharedFiles = async () => {
      try {
        // Check URL for shared files parameter
        const params = new URLSearchParams(window.location.search);
        const sharedFilesParam = params.get('sharedFiles');
        
        if (!sharedFilesParam) {
          return;
        }
        
        console.log('[UploadModal] Loading shared files:', sharedFilesParam);
        
        const fileIds = sharedFilesParam.split(',');
        sharedFileIdsRef.current = fileIds;
        
        // Load files from IndexedDB
        const files = await getSharedFiles(fileIds);
        
        if (files.length > 0) {
          console.log('[UploadModal] Loaded', files.length, 'shared files');
          // Process the first file automatically
          processFile(files[0]);
          
          // TODO: If multiple files, could show a queue or handle batch upload
          if (files.length > 1) {
            console.log('[UploadModal] Multiple files shared, using first one');
          }
        } else {
          console.warn('[UploadModal] No files retrieved from IndexedDB');
        }
      } catch (error) {
        console.error('[UploadModal] Error loading shared files:', error);
      }
    };
    
    loadSharedFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup shared files from IndexedDB
  const cleanupSharedFiles = async () => {
    if (sharedFileIdsRef.current.length > 0) {
      console.log('[UploadModal] Cleaning up shared files:', sharedFileIdsRef.current);
      await deleteSharedFiles(sharedFileIdsRef.current);
      sharedFileIdsRef.current = [];
    }
  };

  // Wrapper for onClose to cleanup shared files
  const handleClose = async () => {
    await cleanupSharedFiles();
    onClose();
  };

  // Wrapper for onUploadComplete to cleanup shared files
  const handleUploadComplete = async () => {
    await cleanupSharedFiles();
    onUploadComplete();
  };

  // Resilient file reading with retry logic
  const readFileWithRetry = async (file: File, maxRetries = 3): Promise<ArrayBuffer> => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // Try modern arrayBuffer first
        return await file.arrayBuffer();
      } catch (error) {
        console.warn(`Attempt ${attempt + 1}/${maxRetries} to read file failed:`, error);
        
        // Try FileReader fallback
        try {
          return await new Promise<ArrayBuffer>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target?.result as ArrayBuffer);
            reader.onerror = () => reject(new Error('FileReader failed'));
            reader.readAsArrayBuffer(file);
          });
        } catch (fallbackError) {
          // If this is the last attempt, throw
          if (attempt === maxRetries - 1) {
            throw new Error('Could not read file after multiple attempts. Please try selecting a different photo or take a new one.');
          }
          // Wait before retrying (exponential backoff: 500ms, 1000ms, 2000ms)
          await new Promise(resolve => setTimeout(resolve, 500 * Math.pow(2, attempt)));
        }
      }
    }
    throw new Error('Failed to read file');
  };

  const processFile = async (selectedFile: File) => {
    // Validate file type
    if (!selectedFile.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    setFile(selectedFile);
    setError('');
    setDuplicate(false);
    setIsDragging(false);
    setIsProcessingFile(true);

    try {
      // RESILIENCE: Cache file data immediately to prevent mobile permission expiration
      console.log('📁 Reading and caching file data to prevent permission issues...');
      const fileData = await readFileWithRetry(selectedFile);
      
      // Store as Blob (more memory efficient than ArrayBuffer)
      const cachedBlob = new Blob([fileData], { type: selectedFile.type });
      setCachedFileData(cachedBlob);
      console.log('✅ File data cached successfully');

      // Create preview from cached data
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
        setIsProcessingFile(false);
      };
      reader.onerror = () => {
        setError('Failed to create preview. Please try again.');
        setIsProcessingFile(false);
      };
      reader.readAsDataURL(cachedBlob);
      
    } catch (err) {
      console.error('Failed to process file:', err);
      setError(err instanceof Error ? err.message : 'Failed to read file. Please try again.');
      setIsProcessingFile(false);
      setFile(null);
      setCachedFileData(null);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    processFile(selectedFile);
  };

  const handleCropComplete = (croppedBlob: Blob) => {
    if (!file) return;

    // CRITICAL: Revoke old preview URL to prevent mobile browser caching issues
    if (preview) {
      URL.revokeObjectURL(preview);
    }

    // Create new file from cropped blob
    const croppedFile = new File([croppedBlob], file.name, { type: 'image/jpeg' });
    
    // Update file and cached data
    setFile(croppedFile);
    setCachedFileData(croppedBlob);
    
    // Create new preview URL
    const previewUrl = URL.createObjectURL(croppedBlob);
    setPreview(previewUrl);
    
    // MOBILE FIX: Increment version to force image re-render
    // Mobile browsers cache more aggressively than desktop
    setPreviewVersion(v => v + 1);
    
    // Close crop modal
    setShowCropModal(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      processFile(droppedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError('');
    setProgress(10);

    try {
      // RESILIENCE: Use cached file data if available, fall back to original file
      const fileToHash = cachedFileData 
        ? new File([cachedFileData], file.name, { type: file.type })
        : file;

      // Compute hash with timeout (using native crypto API for speed)
      setProgress(20);
      
      const md5 = await Promise.race([
        computeMD5(fileToHash),
        new Promise<string>((_, reject) => 
          setTimeout(() => reject(new Error('Hash computation timed out after 30 seconds')), 30000)
        )
      ]);

      console.log('File hash (SHA-256):', md5);
      setProgress(40);

      // Get image dimensions
      const dimensions = await getImageDimensions(preview);
      
      // Create upload intent
      const intent = await api.createUploadIntent({
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        md5,
        width: dimensions.width,
        height: dimensions.height,
      });

      setProgress(50);

      // Check for duplicate
      if (intent.duplicate) {
        setDuplicate(true);
        setProgress(100);
        setTimeout(() => {
          handleUploadComplete();
        }, 2000);
        return;
      }

      // Upload to S3 - use cached data if available for reliability
      setProgress(60);
      const fileToUpload = cachedFileData 
        ? new File([cachedFileData], file.name, { type: file.type })
        : file;
      await api.uploadToS3(intent.uploadUrl, fileToUpload);
      
      setProgress(70);
      
      // Add tags if any
      if (tags.length > 0) {
        setProgress(75);
        for (const tag of tags) {
          try {
            await api.addTag(intent.photoId, { tag: tag.value, type: tag.type });
          } catch (err) {
            console.error('Error adding tag:', err);
            // Continue with other tags even if one fails
          }
        }
      }
      
      setProgress(85);
      
      // Add audio message if recorded
      if (audioBlob) {
        try {
          const messageResponse = await api.createMessage(intent.photoId, {
            durationSeconds: recordingTime,
          });
          await api.uploadToS3(
            messageResponse.uploadUrl,
            new File([audioBlob], 'audio.webm', { type: 'audio/webm' })
          );
        } catch (err) {
          console.error('Error adding audio message:', err);
          // Continue even if audio upload fails
        }
      }
      
      setProgress(100);
      
      // Clean up cached data after successful upload
      setCachedFileData(null);
      
      // Wait a bit for thumbnail generation, then close
      setTimeout(() => {
        handleUploadComplete();
      }, 1500);
      
    } catch (err) {
      console.error('Upload error:', err);
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to upload image';
      
      if (err instanceof Error) {
        // Handle specific error types
        if (err.message.includes('permission') || err.message.includes('read')) {
          errorMessage = 'Could not read the selected file. On mobile, please try: 1) Taking a new photo, or 2) Re-selecting the image from your gallery.';
        } else if (err.message.includes('timeout') || err.message.includes('timed out')) {
          errorMessage = 'Upload took too long. Please check your internet connection and try again.';
        } else if (err.message.includes('Network')) {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      setProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const getImageDimensions = (src: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.src = src;
    });
  };

  // Audio recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setHasAudio(true);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= 60) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      console.error('Error accessing microphone:', err);
      setError('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const playPreview = () => {
    if (audioBlob && audioPreviewRef.current) {
      const url = URL.createObjectURL(audioBlob);
      audioPreviewRef.current.src = url;
      audioPreviewRef.current.play();
    }
  };

  const removeAudio = () => {
    setAudioBlob(null);
    setHasAudio(false);
    setRecordingTime(0);
    if (audioPreviewRef.current) {
      audioPreviewRef.current.pause();
      audioPreviewRef.current.src = '';
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    const timer = timerRef.current;
    const mediaRecorder = mediaRecorderRef.current;
    const audioPreview = audioPreviewRef.current;
    
    return () => {
      if (timer) {
        clearInterval(timer);
      }
      if (mediaRecorder && isRecording) {
        mediaRecorder.stop();
      }
      if (audioPreview) {
        audioPreview.pause();
      }
    };
  }, [isRecording]);

  // Tag autocomplete handlers
  const updateTagSuggestions = (value: string, type: 'person' | 'place' | 'thing') => {
    const searchValue = value.toLowerCase().trim();
    
    if (searchValue.length > 0) {
      const matchingTags = existingTags
        .filter((t) => {
          const tagLower = t.tag.toLowerCase();
          return t.type === type && 
            tagLower.includes(searchValue) &&
            tagLower !== searchValue;
        })
        .map((t) => t.tag)
        .slice(0, 8);
      
      setTagSuggestions(matchingTags);
      setShowTagSuggestions(matchingTags.length > 0);
    } else {
      // Show popular tags of this type when field is empty
      const popularTags = existingTags
        .filter((t) => t.type === type)
        .sort((a, b) => b.count - a.count)
        .map((t) => t.tag)
        .slice(0, 8);
      
      setTagSuggestions(popularTags);
      setShowTagSuggestions(popularTags.length > 0);
    }
  };

  const handleTagChange = (value: string) => {
    setNewTag(value);
    setSelectedSuggestionIndex(-1);
    updateTagSuggestions(value, newTagType);
  };

  const handleTagTypeChange = (type: 'person' | 'place' | 'thing') => {
    setNewTagType(type);
    setSelectedSuggestionIndex(-1);
    updateTagSuggestions(newTag, type);
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showTagSuggestions) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < tagSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      
      case 'Enter':
        if (selectedSuggestionIndex >= 0) {
          e.preventDefault();
          selectSuggestion(tagSuggestions[selectedSuggestionIndex]);
        }
        break;
      
      case 'Escape':
        setShowTagSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  const selectSuggestion = (tag: string) => {
    setNewTag(tag);
    setShowTagSuggestions(false);
    setSelectedSuggestionIndex(-1);
  };

  // Tag management functions
  const addTag = () => {
    if (newTag.trim()) {
      // Normalize tag to lowercase for consistency
      const normalizedTag = newTag.trim().toLowerCase();
      setTags([...tags, { value: normalizedTag, type: newTagType }]);
      setNewTag('');
      setShowTagSuggestions(false);
    }
  };

  const removeTag = (index: number) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">Upload Photo</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {duplicate && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
              ✨ This photo already exists in the album! Showing existing photo.
            </div>
          )}

          {!file ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
                isDragging
                  ? 'border-primary-500 bg-primary-50 scale-105'
                  : 'border-gray-300 hover:border-primary-500 hover:bg-primary-50/50'
              }`}
            >
              <div className="text-6xl mb-4">{isDragging ? '📂' : '📸'}</div>
              <p className="text-lg font-medium text-gray-700 mb-2">
                {isDragging ? 'Drop your photo here' : 'Click to select a photo'}
              </p>
              <p className="text-sm text-gray-500">
                or drag and drop an image file here
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Show loading indicator while processing file */}
              {isProcessingFile && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-xl">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600">
                      📁 Securing file access...
                    </p>
                  </div>
                </div>
              )}
              
              <div className="relative rounded-xl overflow-hidden">
                <img
                  key={`preview-${previewVersion}`}
                  src={preview}
                  alt="Preview"
                  className="w-full h-auto max-h-96 object-contain bg-gray-100"
                />
              </div>

              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>{file.name}</span>
                <span>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
              </div>

              {/* Tags Section */}
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  🏷️ Add Tags (optional)
                </label>
                
                {/* Tag Type Selection */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {(['person', 'place', 'thing'] as const).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handleTagTypeChange(type)}
                      disabled={uploading}
                      className={`px-3 py-2.5 font-semibold rounded-lg transition-all ${
                        newTagType === type
                          ? 'bg-blue-600 border-2 border-blue-700 text-white shadow-md scale-105'
                          : 'bg-white border-2 border-gray-300 text-gray-800 hover:border-blue-400 hover:bg-gray-50 disabled:opacity-50'
                      }`}
                    >
                      <div className={`text-xl mb-0.5 ${newTagType === type ? 'filter brightness-125' : ''}`}>
                        {type === 'person' ? '👤' : type === 'place' ? '📍' : '🎯'}
                      </div>
                      <div className={`capitalize text-xs ${newTagType === type ? 'text-white font-bold' : 'text-gray-700'}`}>
                        {type}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Tag Input with Autocomplete */}
                <div className="relative mb-3">
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={newTag}
                        onChange={(e) => handleTagChange(e.target.value)}
                        onKeyDown={handleTagKeyDown}
                        onKeyPress={(e) => e.key === 'Enter' && !showTagSuggestions && addTag()}
                        onFocus={() => {
                          if (newTag.length === 0) {
                            updateTagSuggestions('', newTagType);
                          }
                        }}
                        onBlur={() => {
                          setTimeout(() => setShowTagSuggestions(false), 200);
                        }}
                        placeholder="e.g., Summer, Paris, Birthday..."
                        disabled={uploading}
                        className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white disabled:opacity-50"
                        autoComplete="off"
                      />
                      
                      {/* Tag Suggestions Dropdown */}
                      {showTagSuggestions && (
                        <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto z-[60]">
                          {tagSuggestions.length === 0 ? (
                            <div className="px-4 py-2 text-sm text-gray-500">
                              No matching tags
                            </div>
                          ) : (
                            tagSuggestions.map((tag, index) => {
                              const tagData = existingTags.find((t) => t.tag === tag);
                              const usageCount = tagData?.count || 0;
                              
                              return (
                                <button
                                  key={tag}
                                  type="button"
                                  onClick={() => selectSuggestion(tag)}
                                  className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors flex items-center justify-between ${
                                    index === selectedSuggestionIndex ? 'bg-gray-100' : ''
                                  }`}
                                >
                                  <span className="text-sm font-medium">{tag}</span>
                                  <span className="text-xs text-gray-400">
                                    {usageCount} photo{usageCount !== 1 ? 's' : ''}
                                  </span>
                                </button>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={addTag}
                      disabled={uploading || !newTag.trim()}
                      className="btn-primary px-4 py-2 text-sm disabled:opacity-50 whitespace-nowrap"
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* Display Tags */}
                {tags.length > 0 && (
                  <div className="space-y-2">
                    {(['person', 'place', 'thing'] as const).map((type) => {
                      const typeTags = tags.filter((t) => t.type === type);
                      if (typeTags.length === 0) return null;
                      
                      const icon = type === 'person' ? '👤' : type === 'place' ? '📍' : '🎯';
                      
                      return (
                        <div key={type} className="space-y-1">
                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            {icon} {type}s
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {typeTags.map((tag, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 text-gray-700 border border-gray-200"
                              >
                                {tag.value}
                                <button
                                  onClick={() => removeTag(tags.indexOf(tag))}
                                  disabled={uploading}
                                  className="text-gray-400 hover:text-red-600 transition-colors ml-0.5 disabled:opacity-50"
                                  title="Remove tag"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Audio Recording Section */}
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🎤 Add Audio Message (optional)
                </label>
                <div className="space-y-3">
                  {!hasAudio && !isRecording && (
                    <button
                      onClick={startRecording}
                      disabled={uploading}
                      className="btn-danger w-full justify-center py-2 disabled:opacity-50 flex items-center gap-2"
                    >
                      <span className="text-xl">🎤</span>
                      Start Recording
                    </button>
                  )}
                  
                  {isRecording && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-center gap-3">
                        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                        <span className="text-lg font-mono font-semibold text-red-600">
                          {formatTime(recordingTime)}
                        </span>
                      </div>
                      <button
                        onClick={stopRecording}
                        className="btn-danger w-full justify-center py-2"
                      >
                        Stop Recording
                      </button>
                    </div>
                  )}

                  {hasAudio && !isRecording && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-600">
                          Recording: {formatTime(recordingTime)}
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={playPreview}
                            disabled={uploading}
                            className="btn-primary px-3 py-1 text-sm disabled:opacity-50"
                          >
                            ▶️ Play
                          </button>
                          <button
                            onClick={removeAudio}
                            disabled={uploading}
                            className="btn-secondary px-3 py-1 text-sm"
                          >
                            🗑️ Remove
                          </button>
                        </div>
                      </div>
                      <audio ref={audioPreviewRef} className="hidden" />
                    </div>
                  )}
                </div>
              </div>

              {uploading && (
                <div className="space-y-2">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-rose-500 via-fuchsia-500 to-indigo-500 transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-600 text-center">
                    {progress < 40 && 'Computing checksum...'}
                    {progress >= 40 && progress < 60 && 'Uploading photo...'}
                    {progress >= 60 && progress < 85 && 'Adding tags and audio...'}
                    {progress >= 85 && 'Processing...'}
                  </p>
                </div>
              )}

              {/* Crop Button - Optional */}
              {!uploading && !isProcessingFile && (
                <button
                  onClick={() => setShowCropModal(true)}
                  className="w-full px-4 py-2.5 mb-3 bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 border-2 border-purple-200 hover:border-purple-300 text-purple-700 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                >
                  <span className="text-lg">✂️</span>
                  <span>Crop Photo (Optional)</span>
                </button>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setFile(null);
                    setPreview('');
                    setError('');
                    setTags([]);
                    setNewTag('');
                    removeAudio();
                    // Clear cached file data
                    setCachedFileData(null);
                    setIsProcessingFile(false);
                  }}
                  disabled={uploading || isProcessingFile}
                  className="btn-secondary flex-1"
                >
                  Change Photo
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading || isProcessingFile}
                  className="btn-primary flex-1 justify-center py-2 disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Crop Modal */}
      {showCropModal && preview && (
        <CropModal
          imageSrc={preview}
          onClose={() => setShowCropModal(false)}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  );
}

